<script lang="ts">
    import { getDashboardCtx } from "../../state/dashboard.svelte";
    import { translate } from "@/utils/format";
    import { Spinner } from "@/components";
    import { resolvePartnerPortalSE } from "@/services/referrals";
    import { sendSlackCaloo } from "@/services/logging";

    const store = getDashboardCtx();

    type LinkClientStep =
        | "idle"
        | "form"
        | "loading"
        | "linked"
        | "already"
        | "notRegistered"
        | "error";
    let linkClientStep = $state<LinkClientStep>("idle");
    let linkClientEmail = $state("");
    let linkClientError = $state("");
    let linkClientAssignedNow = $state(false);

    function isValidEmail(v: string) {
        const s = String(v || "").trim();
        if (!s) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    }

    function openLinkClient() {
        linkClientStep = "form";
        linkClientEmail = "";
        linkClientError = "";
        linkClientAssignedNow = false;
    }

    function closeLinkClient() {
        linkClientStep = "idle";
        linkClientEmail = "";
        linkClientError = "";
    }

    async function submitLinkClient() {
        const email = linkClientEmail.trim();
        if (!email || !isValidEmail(email)) {
            linkClientError = translate(
                "PP_ERROR_INVALID_EMAIL",
                "Introduce un email válido.",
            );
            linkClientStep = "error";
            return;
        }

        linkClientStep = "loading";
        linkClientError = "";

        try {
            const res = await resolvePartnerPortalSE(
                store.referralCodeTrimmed,
                email,
            );

            if (!res.ok) {
                if (res.reason === "user_not_registered") {
                    linkClientStep = "notRegistered";
                    return;
                }
                linkClientError =
                    String(res.reason || "").trim() ||
                    translate(
                        "PP_ERROR_GENERIC",
                        "Ha ocurrido un error. Inténtalo de nuevo.",
                    );
                linkClientStep = "error";
                return;
            }

            // If store.user already belongs to another partner (redirect case)
            if (
                res.shouldRedirect &&
                res.resolvedReferralId &&
                res.resolvedReferralId !== store.referralCodeTrimmed
            ) {
                linkClientStep = "already";
                return;
            }

            linkClientAssignedNow = !!res.assignedNow;
            linkClientStep = "linked";

            // Slack notification
            if (res.assignedNow) {
                try {
                    const uid = res.user?.uid || "";
                    const msg = `Partner ${store.referralCodeTrimmed} linked client manually: (id: ${uid} – email: ${email})`;
                    await sendSlackCaloo(uid || store.user?.id || "dashboard", msg);
                } catch {
                    /* non-blocking */
                }
            }

            // Refresh metrics after linking
            store.loadMetrics();
        } catch (err: any) {
            const data = err?.response?.data;
            const reason = String(data?.reason || "").trim();

            if (reason === "user_not_registered") {
                linkClientStep = "notRegistered";
                return;
            }
            linkClientError = translate(
                "PP_ERROR_GENERIC",
                "Ha ocurrido un error. Inténtalo de nuevo.",
            );
            linkClientStep = "error";
        }
    }

</script>

<div class="referrals__linkClient">
    {#if linkClientStep === "idle"}
        <button
            type="button"
            class="linkClient__trigger"
            onclick={openLinkClient}
        >
            <i class="fal fa-user-plus"></i>
            <span
                >{translate(
                    "PARTNER_LINK_CLIENT_BTN",
                    "Vincular cliente",
                )}</span
            >
        </button>
    {:else if linkClientStep === "form" || linkClientStep === "error"}
        <div class="linkClient__form">
            <h4>
                {translate(
                    "PARTNER_LINK_CLIENT_TITLE",
                    "Vincular cliente",
                )}
            </h4>
            <p class="linkClient__desc">
                {translate(
                    "PARTNER_LINK_CLIENT_DESC",
                    "Introduce el email del cliente para vincularlo a tu cuenta de partner.",
                )}
            </p>
            <div class="linkClient__inputRow">
                <input
                    type="email"
                    placeholder={translate(
                        "PARTNER_LINK_CLIENT_PLACEHOLDER",
                        "Email del cliente",
                    )}
                    bind:value={linkClientEmail}
                    onkeydown={(e: KeyboardEvent) => {
                        if (e.key === "Enter") submitLinkClient();
                    }}
                />
                <button
                    type="button"
                    class="button --primary"
                    onclick={submitLinkClient}
                    disabled={!linkClientEmail.trim()}
                >
                    {translate(
                        "PARTNER_LINK_CLIENT_SEND",
                        "Vincular",
                    )}
                </button>
            </div>
            {#if linkClientStep === "error" && linkClientError}
                <p class="linkClient__error">{linkClientError}</p>
            {/if}
            <button
                type="button"
                class="linkClient__cancel"
                onclick={closeLinkClient}
            >
                {translate("PARTNER_CANCEL", "Cancelar")}
            </button>
        </div>
    {:else if linkClientStep === "loading"}
        <div class="linkClient__result">
            <Spinner
                text={translate(
                    "PARTNER_LINK_CLIENT_LOADING",
                    "Vinculando...",
                )}
            />
            <p>
                {translate(
                    "PARTNER_LINK_CLIENT_LOADING",
                    "Vinculando...",
                )}
            </p>
        </div>
    {:else if linkClientStep === "linked"}
        <div class="linkClient__result linkClient__result--success">
            <i class="fal fa-check-circle"></i>
            {#if linkClientAssignedNow}
                <p>
                    {translate(
                        "PARTNER_LINK_CLIENT_SUCCESS",
                        "Cliente vinculado correctamente.",
                    )}
                </p>
            {:else}
                <p>
                    {translate(
                        "PARTNER_LINK_CLIENT_ALREADY_YOURS",
                        "Este cliente ya estaba vinculado a tu cuenta.",
                    )}
                </p>
            {/if}
            <button
                type="button"
                class="linkClient__action"
                onclick={closeLinkClient}
            >
                {translate(
                    "PARTNER_LINK_CLIENT_ANOTHER",
                    "Vincular otro",
                )}
            </button>
        </div>
    {:else if linkClientStep === "already"}
        <div class="linkClient__result linkClient__result--warn">
            <i class="fal fa-exclamation-triangle"></i>
            <p>
                {translate(
                    "PARTNER_LINK_CLIENT_OTHER_PARTNER",
                    "Este cliente ya está vinculado a otro partner.",
                )}
            </p>
            <button
                type="button"
                class="linkClient__action"
                onclick={closeLinkClient}
            >
                {translate("PARTNER_LINK_CLIENT_CLOSE", "Cerrar")}
            </button>
        </div>
    {:else if linkClientStep === "notRegistered"}
        <div class="linkClient__result linkClient__result--info">
            <i class="fal fa-info-circle"></i>
            <p>
                {translate(
                    "PARTNER_LINK_CLIENT_NOT_REGISTERED",
                    "Este email no tiene cuenta en Caloo. El cliente debe descargar la app y registrarse primero.",
                )}
            </p>
            <button
                type="button"
                class="linkClient__action"
                onclick={closeLinkClient}
            >
                {translate("PARTNER_LINK_CLIENT_CLOSE", "Cerrar")}
            </button>
        </div>
    {/if}
</div>
